import _ from 'lodash';
import React, { useState } from "react";
import { generateClient } from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
const { v4: uuidv4 } = require('uuid');
import { getSubjects } from '@/graphql/small-queries';
import { connectVoice } from '@/graphql/mutations';
import Modal from '@/Modal';

const client = generateClient();

const getObjects = ({ id, filters: _filters, limit, offset, search, sorting }) => {
	const filters = [];
	if (search?.length > 0) {
		const search_num = Number(search);
		filters.push(...[
			// Test number fields only if search conforms to a Number
			...(!isNaN(search_num) ? [
				{ ccid: { eq: search_num } },
				{ id: { eq: search_num } }
			] : []),
			{ full_name_first: { like: `%${search}%` } },
			{ subject_emails: [{ email_address: { like: `%${search}%` } }] },
			{ subject_phone_numbers: [{ phone_number: { like: `%${search}%` } }] }
		]);
	}
	return client.graphql({ query: getSubjects, variables: { id, ..._filters, filters, limit, offset, sorting } })
		.then(response => {
			const { subjects: data, total_count, ...raw } = response.data.getSubjects;
			data.forEach(e => {
				const v = (e.subject_enrollment_status || []).map(f => f.enrollment_status);
				e.enrollment_status = v.map(f => {
					const parts = f.split('::');
					return parts.length > 1 ? parts[1] : parts[0];
				}).join('\n');
			});
			return {
				data,
				raw: { ...raw, total: total_count }
			};
		});
};

export default ({ catchError, channel: _channel = 'study_config_voice', data, flowId = '6c515d98-47ba-4312-b84a-a1dfc6a7ae88', onClose, setData, setModal } = {}) => {
	const [channel, set_channel] = useState(_channel);
	const [use_pre_call_text, set_use_pre_call_text] = useState(true);
	const [error, setError] = useState();
	const name = `${data.first_name} ${data.last_name}`;
	const phone_number = _.orderBy(data.subject_phone_numbers || [], 'created_at')?.[0];
	if (!phone_number) {
		catchError && catchError('No phone number');
		return;
	}
	// CPG-318 Determinative chooser of study
	// Only consider those which are not complete
	const ses_not_complete = _.sortBy(data.subject_enrollment_status, 'created_at').filter(e => !e.study_enrollment_status_is_complete);
	const ses = ses_not_complete?.[0];
	if (!ses || !ses.study_id) {
		catchError && catchError('Must be enrolled in a study');
		return;
	}
	const _onAction = async () => {
		try {
			// Get fresh data
			const _data = (await getObjects({ id: data.id })).data[0];
			if (!_data) {
				throw 'Could not retrieve data';
			}
			// An opportunity to update the List
			setData && setData([_data]);
			// Use the contact_flow_id override if not null
			// Use the ava overrides if not null -- contact_flow_id winner takes all
			let _flowId = flowId;
			let _ava_bot_alias_id;
			let _ava_bot_config;
			let _ava_bot_id;
			let _ava_state_machine;
			if (ses.contact_flow_id) {
				_flowId = ses.contact_flow_id;
				_ava_bot_alias_id = ses.ava_bot_alias_id;
				_ava_bot_config = ses.ava_bot_config;
				_ava_bot_id = ses.ava_bot_id;
				_ava_state_machine = ses.ava_state_machine;
			} else if (ses.study_contact_flow_id) {
				_flowId = ses.study_contact_flow_id;
				_ava_bot_alias_id = ses.study_ava_bot_alias_id;
				_ava_bot_config = ses.study_ava_bot_config;
				_ava_bot_id = ses.study_ava_bot_id;
				_ava_state_machine = ses.study_ava_state_machine;
			}
			// Call_Subject is install specific
			let use_alias = process.env.USER_BRANCH || 'dev';
			// FIXME: Fixup for Amplify not allowing numbers
			if (use_alias === 'ktwo') use_alias = 'k2';
			if (use_alias === 'qctprod') use_alias = 'qct_prod';
			return client.graphql({
				query: connectVoice, variables: {
					input: {
						attributes: [
							{ key: 'subject_id', value: `${data.id}` },
							{ key: 'phone_number', value: phone_number.phone_number },
							{ key: 'title', value: _data.title || '' },
							{ key: 'first_name', value: _data.first_name || '' },
							{ key: 'middle_name', value: _data.middle_name || '' },
							{ key: 'last_name', value: _data.last_name || '' },
							{ key: 'date_of_birth', value: _data.date_of_birth || '' },
							{ key: 'preferred_language', value: _data.preferred_language || null },
							{ key: 'subject_ccid', value: `${_data.ccid}` },
							{ key: 'subject_enrollment_status', value: ses.enrollment_status },
							{ key: 'subject_enrollment_status_ccid', value: `${ses.ccid}` },
							{ key: 'subject_enrollmentid', value: `${ses.ccid}` },
							{ key: 'study_ccid', value: `${ses.study_ccid}` },
							{ key: 'study_id', value: `${ses.study_id}` },
							{ key: 'study_name', value: ses.study_name },
							{ key: 'study_visit_name', value: ses.study_visit_name },
							{ key: 'visit_name', value: ses.study_visit_name },
							{ key: 'ava_bot_alias_id', value: _ava_bot_alias_id || '' },
							{ key: 'ava_bot_config', value: _ava_bot_config || '' },
							{ key: 'ava_bot_id', value: _ava_bot_id || '' },
							{ key: 'ava_state_machine', value: _ava_state_machine || '' },
							// A hint which goes into subject_contacts
							{ key: 'purpose', value: 'appointment' }
						],
						channel,
						client_token: uuidv4(),
						...(flowId ? { contact_flow_id: _flowId } : undefined),
						phone_number: phone_number.phone_number,
						...(use_pre_call_text ? { step_function: `arn:aws:states:us-east-1:445567080072:stateMachine:Call_Subject:${use_alias}` } : undefined)
					}
				}
			});
		} catch (error) {
			setError(new Error(error));
		}
	};
	const infoIcon = (<FontAwesomeIcon className="text-primary" icon={icons_solid.faCircleInfo} size="lg" />);
	const dangerIcon = (<FontAwesomeIcon className="text-danger" icon={icons_solid.faCircleExclamation} size="lg" />);
	const checkIcon = (<FontAwesomeIcon className="text-success" icon={icons_solid.faCheck} size="lg" />);
	const action = channel === 'study_config_sms' ? 'Text' : 'Call';
	return (
		<Modal
			footer={
				<>
					<button className="btn btn-danger" data-bs-dismiss="modal" onClick={_onAction}>{action} {name}</button>
					<button className="btn btn-link" data-bs-dismiss="modal">Cancel</button>
				</>
			}
			isOpen={true}
			onClose={onClose}
			size="md"
			title={<span>{action} <u><b>{name}</b></u>?</span>}
		>
			<div
				className="p-3"
				onClick={event => event.stopPropagation()}
			>
				<p>{infoIcon}<b className="ms-2 text-primary">INFO</b></p>
				<p>This {action.toLowerCase()} will be made to {phone_number.phone_number}.</p>
				<hr />
				<p><b>Available studies</b></p>
				<ul className="ps-0">
					{ses_not_complete.map((e, i) => (
						<li key={i} style={{ listStyleType: 'none' }}>
							{i === 0 ? (
								<>
									{checkIcon}
									<span style={{ marginLeft: '0.5rem' }}>{e.study_name} ({e.enrollment_status.replace('::', ' / ')})</span>
								</>
							) : (
								<span style={{ marginLeft: '1.5rem' }}>{e.study_name} ({e.enrollment_status.replace('::', ' / ')})</span>
							)}
						</li>
					))}
				</ul>
				<hr />
				<p><b>Include pre-call text?</b></p>
				<div className="form-check">
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={use_pre_call_text} name="use_pre_call_text" onChange={() => set_use_pre_call_text(true)} type="radio"></input>Yes</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={!use_pre_call_text} name="use_pre_call_text" onChange={() => set_use_pre_call_text(false)} type="radio"></input>No</label><br />
				</div>
				<hr />
				<p><b>How would you like them contacted?</b></p>
				<div className="form-check">
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'study_config_sms'} name="channel" onChange={() => set_channel('study_config_sms')} type="radio"></input>Study Configured Text</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'study_config_voice'} name="channel" onChange={() => set_channel('study_config_voice')} type="radio"></input>Study Configured Voice</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9001'} name="channel" onChange={() => set_channel('twilio_voice_9001')} type="radio"></input>Twilio Voice 9001 (MASH - Nova Sonic)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9002'} name="channel" onChange={() => set_channel('twilio_voice_9002')} type="radio"></input>Twilio Voice 9002 (MASH - OpenAI Realtime)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9003'} name="channel" onChange={() => set_channel('twilio_voice_9003')} type="radio"></input>Twilio Voice 9003 (Manager's choice)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9004'} name="channel" onChange={() => set_channel('twilio_voice_9004')} type="radio"></input>Twilio Voice 9004 (OpenAI Demo)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9005'} name="channel" onChange={() => set_channel('twilio_voice_9005')} type="radio"></input>Twilio Voice 9005 (Dev)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9006'} name="channel" onChange={() => set_channel('twilio_voice_9006')} type="radio"></input>Twilio Voice 9006 (BFA Dev Nova Sonic)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9007'} name="channel" onChange={() => set_channel('twilio_voice_9007')} type="radio"></input>Twilio Voice 9007 (BFA Prod)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9008'} name="channel" onChange={() => set_channel('twilio_voice_9008')} type="radio"></input>Twilio Voice 9008 (General Purpose)</label><br />
					<label className="form-check-label me-2"><input className="form-check-input mb-2" defaultChecked={channel === 'twilio_voice_9100'} name="channel" onChange={() => set_channel('twilio_voice_9100')} type="radio"></input>Twilio Voice 9100 (MASH - Failover)</label><br />
				</div>
			</div>
		</Modal>
	);
};
